import { useMemo, useState } from 'react';
import type { Order } from '../../types/ordering.types';
import { OrderStatus, formatOrderAmount, OrderCurrency } from '../../types/ordering.types';
import type { Person } from '../../types/timesheet.types';
import {
  OrderListTable,
  COL_REF, COL_DATE, COL_DESC, COL_CATEGORY, COL_EXP_DELIVERY, COL_TOTAL, COL_STATUS, COL_LM,
} from './OrderListTable';

type StatusFilter = 'all' | 'draft' | 'pending' | 'approved' | 'rejected' | 'received';

// Orders in the approval chain (from requester's perspective: "pending someone's action")
const PENDING_STATUSES: OrderStatus[] = [
  OrderStatus.Requested,
  OrderStatus.LMApproved,
  OrderStatus.ChemicalApproved,
];

// Orders that are fully approved and being processed/delivered by OM
const APPROVED_STATUSES: OrderStatus[] = [
  OrderStatus.OMApproved,
  OrderStatus.Ordered,
  OrderStatus.PartialDelivery,
];

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all',      label: 'All' },
  { key: 'draft',    label: 'Draft' },
  { key: 'pending',  label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'received', label: 'Received' },
];

interface Props {
  orders: Order[];
  isLoading: boolean;
  onOpenOrder: (order: Order) => void;
  search: string;
  people?: Person[];
}

export function MyOrders({ orders, isLoading, onOpenOrder, search, people }: Props) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const peopleByEmail = useMemo(
    () => new Map((people ?? []).map(p => [p.m365Email.toLowerCase(), p])),
    [people]
  );

  const filtered = useMemo(() => {
    let list = orders;
    if (statusFilter === 'draft')    list = list.filter(o => o.status === OrderStatus.Draft);
    else if (statusFilter === 'pending')  list = list.filter(o => PENDING_STATUSES.includes(o.status));
    else if (statusFilter === 'approved') list = list.filter(o => APPROVED_STATUSES.includes(o.status));
    else if (statusFilter === 'rejected') list = list.filter(o => o.status === OrderStatus.Rejected);
    else if (statusFilter === 'received') list = list.filter(o => o.status === OrderStatus.Delivered);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(o =>
        o.title.toLowerCase().includes(q) ||
        o.orderRef.toLowerCase().includes(q) ||
        o.supplier.toLowerCase().includes(q)
      );
    }
    return list;
  }, [orders, statusFilter, search]);

  // Summary card values
  const openCount = orders.filter(o =>
    ![OrderStatus.Delivered, OrderStatus.Cancelled, OrderStatus.Rejected].includes(o.status)
  ).length;
  // Orders awaiting any approval step (not yet reached OM Group)
  const awaitingApproval = orders.filter(o => PENDING_STATUSES.includes(o.status)).length;
  const now = new Date();
  const mtdApproved = orders
    .filter(o => {
      const d = o.orderDate ? new Date(o.orderDate) : null;
      return d && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear() &&
        o.status !== OrderStatus.Draft && o.status !== OrderStatus.Rejected;
    })
    .reduce((sum, o) => sum + (o.sterlingEquivalent ?? o.totalAmount), 0);
  const ytdSpend = orders
    .filter(o => {
      const d = o.orderDate ? new Date(o.orderDate) : null;
      return d && d.getFullYear() === now.getFullYear() &&
        o.status !== OrderStatus.Draft && o.status !== OrderStatus.Rejected;
    })
    .reduce((sum, o) => sum + (o.sterlingEquivalent ?? o.totalAmount), 0);

  const columns = [
    COL_REF(), COL_DATE('orderDate'), COL_DESC(), COL_CATEGORY(),
    COL_EXP_DELIVERY(), COL_TOTAL(), COL_STATUS(), COL_LM(peopleByEmail),
  ];

  return (
    <div className="ord-view-content">
      {/* v1.9 change banner */}
      {/*<div className="v19-banner">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 18, height: 18, flexShrink: 0, marginTop: 2, color: '#FF6B2B' }}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        <div>
          <strong>v1.9 updates from 16 April scoping session</strong>
          <ul>
            <li>Self-approval ≤£250 · Dual C-team £10k–£20k · CEO on-behalf-of &gt;£20k</li>
            <li>Chemical approval repositioned as last step before OM processing</li>
            <li>Cost centres removed from form — assigned by OM Group</li>
            <li>Multi-currency: GBP, EUR, USD, JPY, NZD, HUF + sterling equivalent</li>
            <li>FYI/Viewer queue · All Orders read-only view · Expected/actual delivery dates</li>
            <li>Supplier is free text (Phase 1) · Kanban dropped in favour of list view</li>
          </ul>
        </div>
      </div>*/}

      {/* Summary Cards */}
      <div className="sum-grid">
        <div className="sum-card">
          <div className="sum-lbl">OPEN ORDERS</div>
          <div className="sum-val">{openCount}</div>
          <div className="sum-sub">{awaitingApproval} awaiting approval</div>
        </div>
        <div className="sum-card c-amber">
          <div className="sum-lbl">PENDING APPROVAL</div>
          <div className="sum-val">{awaitingApproval}</div>
          <div className="sum-sub">in approval chain</div>
        </div>
        <div className="sum-card c-green">
          <div className="sum-lbl">APPROVED MTD</div>
          <div className="sum-val">{formatOrderAmount(mtdApproved, OrderCurrency.GBP)}</div>
          <div className="sum-sub">{now.toLocaleString('en-GB', { month: 'long', year: 'numeric' })}</div>
        </div>
        <div className="sum-card c-accent">
          <div className="sum-lbl">MY SPEND YTD</div>
          <div className="sum-val">{formatOrderAmount(ytdSpend, OrderCurrency.GBP)}</div>
          <div className="sum-sub">across {orders.length} orders</div>
        </div>
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            className={`filter-tab${statusFilter === f.key ? ' active' : ''}`}
            onClick={() => setStatusFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="ord-empty"><div className="spinner" /> Loading orders…</div>
      ) : (
        <OrderListTable
          orders={filtered}
          columns={columns}
          onRowClick={onOpenOrder}
          emptyMessage="No orders found. Click 'New Order' to raise your first request."
        />
      )}
    </div>
  );
}
